<script lang="ts" setup>
import {useLoadingData} from '@/ts/composables/pure/use-loading-data-ref';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {useCreateIntervention} from '@/ts/store/intervention-store';
import InterventionHero from '@/vue/organisms/intervention-landing/intervention-hero.vue';
import InterventionLandingDescription from '@/vue/organisms/intervention-landing/intervention-landing-description.vue';
import InterventionLandingSessions from '@/vue/organisms/intervention-landing/intervention-landing-sessions.vue';
import InterventionLandingGoals from '@/vue/organisms/intervention-landing/intervention-landing-goals.vue';
import InterventionLandingRecap from '@/vue/organisms/intervention-landing/intervention-landing-recap.vue';
import LoadingError from '@/vue/templates/loading-error.vue';
import {onMounted, computed} from 'vue';
import {useRouter} from 'vue-router';
import {PublicationState} from '@/ts/types/dto/interventions-dto';
import {useUserStore} from '@/ts/store/user-store';
import {pagesPath} from '@/ts/router/pages-path';

const {iid} = useRouterSectionParams();

const api = useApi();
const router = useRouter();
const userStore = useUserStore();
const intervention = useCreateIntervention(null, 'intervention-landing-page');

const [fetchIntervention, load, err] = useLoadingData(intervention, async() => api.me.getIntervention(iid.value!));

// Check if user needs medical device consent
const needsMedicalDeviceConsent = computed(() => {
  const consent = userStore.userData?.consents?.medicalDeviceConsent;
  return !consent || !consent.approved || consent.needsReapproval;
});

onMounted(async() => {
  // If consent is needed, redirect to paths list where the modal will show
  if (needsMedicalDeviceConsent.value) {
    void router.replace(pagesPath.intervention.interventionsList);
    return;
  }

  await fetchIntervention();
});

</script>
<template>
  <loading-error id="intervention-landing-page" :loading="load" :error="err" type="page">
    <div v-if="intervention" class="intervention-landing-page">
      <intervention-hero :intervention="intervention"/>
      <intervention-landing-description :description="intervention.description"/>
      <intervention-landing-recap v-if="intervention.publicationState === PublicationState.COMPLETED" :intervention="intervention"/>
      <intervention-landing-sessions v-else :sessions="intervention.sessions"/>
      <intervention-landing-goals :goals="intervention.goals"/>
    </div>
  </loading-error>
</template>
<style lang="scss" scoped>
.intervention-landing-page {
  @include page-alignment;
  display: flex;
  flex-direction: column;
  gap: calc(var(--Spacing-spacing-4) + var(--Spacing-spacing-5));
}
</style>
